/**
 * Zod schemas for Room broadcast messages.
 *
 * These schemas enable runtime validation of messages broadcast by a Room.
 */
import { z } from 'zod'
import { entitySchemas } from './entity.schemas'

// ─────────────────────────────────────────────────────────────────────────────
// Individual Broadcast Message Schemas
// ─────────────────────────────────────────────────────────────────────────────

const publicActiveGameSchema = z.object({
  gameId: z.string(),
  startedAt: z.string(),
  gameOptions: z.record(z.string(), z.unknown()).optional(),
  challengeConfiguration: z.discriminatedUnion('challengeSource', [
    z.object({ challengeSource: z.literal('opponent') }),
    z.object({ challengeSource: z.literal('game'), gameChallengeArrangement: z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace']) }),
  ]).optional(),
  mateIDs: z.array(z.string()).optional(),
  mateSlotMap: z.record(z.string(), z.number()).optional(),
  observerIDs: z.array(z.string()).optional(),
  mateGroups: z.array(z.array(z.string())).optional(),
  playerIds: z.array(z.string()).optional(),
  managed: z.boolean().optional(),
})

const activeGameBroadcastSchema = publicActiveGameSchema.nullable()

const roomStateBroadcastSchema = z.object({
  type: z.literal('roomState'),
  data: z.object({
    mates: z.array(entitySchemas.mateInRoom),
    activeGame: activeGameBroadcastSchema,
  }),
})

const matesFlushedBroadcastSchema = z.object({
  type: z.literal('matesFlushed'),
  data: z.object({}).strict().optional(),
})

const gameStartedBroadcastSchema = z.object({
  type: z.literal('gameStarted'),
  data: z.object({
    game: publicActiveGameSchema,
  }),
})

const gameStateBroadcastSchema = z.object({
  type: z.literal('gameState'),
  data: z.object({
    game: publicActiveGameSchema,
    move: z.unknown().optional(),
    mateId: z.string().optional(),
    seqIndex: z.number().int().nonnegative().optional(),
  }),
})

const stateForcedBroadcastSchema = z.object({
  type: z.literal('stateForced'),
  data: z.object({
    game: publicActiveGameSchema,
    seqIndex: z.number().int().nonnegative(),
  }),
})

const gameEndedBroadcastSchema = z.object({
  type: z.literal('gameEnded'),
  data: z.object({}).strict(),
})

const setupStartedBroadcastSchema = z.object({
  type: z.literal('setupStarted'),
  data: z.object({
    setup: entitySchemas.gameSetup,
    readiness: entitySchemas.setupReadiness,
  }),
})

const setupChangedBroadcastSchema = z.object({
  type: z.literal('setupChanged'),
  data: z.object({
    setup: entitySchemas.gameSetup,
    readiness: entitySchemas.setupReadiness,
  }),
})

const setupCancelledBroadcastSchema = z.object({
  type: z.literal('setupCancelled'),
  data: z.object({}).strict().optional(),
})

const optionsChangedBroadcastSchema = z.object({
  type: z.literal('optionsChanged'),
  data: z.object({
    gameId: z.string(),
    savedOptions: z.unknown(),
  }),
})

const gameChannelPreferenceChangedBroadcastSchema = z.object({
  type: z.literal('gameChannelPreferenceChanged'),
  data: z.object({
    gameId: z.string(),
    channel: z.string(),
  }),
})

const challengeConfigurationPreferenceChangedBroadcastSchema = z.object({
  type: z.literal('challengeConfigurationPreferenceChanged'),
  data: z.object({
    gameId: z.string(),
    challengeConfiguration: z.discriminatedUnion('challengeSource', [
      z.object({ challengeSource: z.literal('opponent') }),
      z.object({ challengeSource: z.literal('game'), gameChallengeArrangement: z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace']) }),
    ]),
  }),
})

const gamelessModeChangedBroadcastSchema = z.object({
  type: z.literal('gamelessModeChanged'),
  data: z.object({
    gameless: z.boolean(),
  }),
})

const curationChangedBroadcastSchema = z.object({
  type: z.literal('curationChanged'),
  data: z.object({
    curated: z.boolean(),
    curatedGameIds: z.array(z.string()),
  }),
})

const hostingChangedBroadcastSchema = z.object({
  type: z.literal('hostingChanged'),
  data: z.object({
    hostMateIds: z.array(z.string()),
    managed: z.boolean(),
    hostTerm: z.string(),
  }),
})

// ─────────────────────────────────────────────────────────────────────────────
// Unified RoomBroadcast Schema
// ─────────────────────────────────────────────────────────────────────────────

const roomBroadcastSchema = z.discriminatedUnion('type', [
  roomStateBroadcastSchema,
  matesFlushedBroadcastSchema,
  gameStartedBroadcastSchema,
  gameStateBroadcastSchema,
  stateForcedBroadcastSchema,
  gameEndedBroadcastSchema,
  setupStartedBroadcastSchema,
  setupChangedBroadcastSchema,
  setupCancelledBroadcastSchema,
  optionsChangedBroadcastSchema,
  gameChannelPreferenceChangedBroadcastSchema,
  challengeConfigurationPreferenceChangedBroadcastSchema,
  gamelessModeChangedBroadcastSchema,
  hostingChangedBroadcastSchema,
  curationChangedBroadcastSchema,
])

// ─────────────────────────────────────────────────────────────────────────────
// Exported Schema Object
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Broadcast schemas for runtime validation.
 */
export const broadcastSchemas = {
  activeGame: activeGameBroadcastSchema,
  roomState: roomStateBroadcastSchema,
  matesFlushed: matesFlushedBroadcastSchema,
  gameStarted: gameStartedBroadcastSchema,
  gameState: gameStateBroadcastSchema,
  stateForced: stateForcedBroadcastSchema,
  gameEnded: gameEndedBroadcastSchema,
  setupStarted: setupStartedBroadcastSchema,
  setupChanged: setupChangedBroadcastSchema,
  setupCancelled: setupCancelledBroadcastSchema,
  optionsChanged: optionsChangedBroadcastSchema,
  gameChannelPreferenceChanged: gameChannelPreferenceChangedBroadcastSchema,
  challengeConfigurationPreferenceChanged: challengeConfigurationPreferenceChangedBroadcastSchema,
  gamelessModeChanged: gamelessModeChangedBroadcastSchema,
  hostingChanged: hostingChangedBroadcastSchema,
  curationChanged: curationChangedBroadcastSchema,
  /** Discriminated union of all broadcast types */
  roomBroadcast: roomBroadcastSchema,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Inferred Types
// ─────────────────────────────────────────────────────────────────────────────

export type ActiveGameBroadcast = z.infer<typeof activeGameBroadcastSchema>
export type RoomStateBroadcast = z.infer<typeof roomStateBroadcastSchema>
export type MatesFlushedBroadcast = z.infer<typeof matesFlushedBroadcastSchema>
export type GameStartedBroadcast = z.infer<typeof gameStartedBroadcastSchema>
export type GameStateBroadcast = z.infer<typeof gameStateBroadcastSchema>
export type StateForcedBroadcast = z.infer<typeof stateForcedBroadcastSchema>
export type GameEndedBroadcast = z.infer<typeof gameEndedBroadcastSchema>
export type SetupStartedBroadcast = z.infer<typeof setupStartedBroadcastSchema>
export type SetupChangedBroadcast = z.infer<typeof setupChangedBroadcastSchema>
export type SetupCancelledBroadcast = z.infer<typeof setupCancelledBroadcastSchema>
export type OptionsChangedBroadcast = z.infer<typeof optionsChangedBroadcastSchema>
export type GameChannelPreferenceChangedBroadcast = z.infer<typeof gameChannelPreferenceChangedBroadcastSchema>
export type ChallengeConfigurationPreferenceChangedBroadcast = z.infer<typeof challengeConfigurationPreferenceChangedBroadcastSchema>
export type GamelessModeChangedBroadcast = z.infer<typeof gamelessModeChangedBroadcastSchema>
export type HostingChangedBroadcast = z.infer<typeof hostingChangedBroadcastSchema>
export type CurationChangedBroadcast = z.infer<typeof curationChangedBroadcastSchema>
export type RoomBroadcast = z.infer<typeof roomBroadcastSchema>
